import { backendServices } from '../../../../services';
import { FieldDictionaryRecordType, FieldDictionaryUsedBy, RecursivePartial } from '../../../../types';
import { FieldDictionaryViewModelWithMetadata } from '../FieldDictionaryEditorContext/types';

export const supportedRecordTypes = [
    FieldDictionaryRecordType.BuyerLevelOverride,
    FieldDictionaryRecordType.WorkflowLevelOverride,
] as const;

function fieldMapper(
    field: backendServices.ViewModels.FieldDictionaryViewModel,
    dirtyFields: FieldDictionaryViewModelWithMetadata[],
    buyerCompanyId: number,
    supplierCompanyId: number | null,
    outputRecordType: FieldDictionaryRecordType,
    workflowFieldDictionaryTemplateId: number | null,
    isPrintPreviewOutput: boolean
): RecursivePartial<backendServices.ViewModels.FieldDictionaryViewModel> {
    const dirtyFieldWithMetadata = dirtyFields.find((df) => df.FieldDictionaryId === field.FieldDictionaryId);

    if (!dirtyFieldWithMetadata) {
        return field;
    }
    const { metadata, ...dirtyField } = dirtyFieldWithMetadata;

    const isMasterField = field.RecordType === FieldDictionaryRecordType.Master;
    const isBuyerLevelOverride = field.RecordType === FieldDictionaryRecordType.BuyerLevelOverride;
    const isWorkflowLevelOverride = field.RecordType === FieldDictionaryRecordType.WorkflowLevelOverride;

    if (outputRecordType === FieldDictionaryRecordType.WorkflowLevelOverride) {
        if (isMasterField) {
            return {
                ...dirtyField,
                FieldDictionaryId: undefined,
                RecordType: FieldDictionaryRecordType.WorkflowLevelOverride,
                RecordType_US: 'Workflow-Level Override',
                MasterFieldDictionaryId: field.FieldDictionaryId,
                WorkflowFieldDictionaryTemplateId: workflowFieldDictionaryTemplateId,
                // These are not editable and might not match across instances of the same field
                LayoutOverrides: field.LayoutOverrides,
            };
        }

        if (isBuyerLevelOverride) {
            return {
                ...dirtyField,
                FieldDictionaryId: undefined,
                RecordType: FieldDictionaryRecordType.WorkflowLevelOverride,
                RecordType_US: 'Workflow-Level Override',
                WorkflowFieldDictionaryTemplateId: workflowFieldDictionaryTemplateId,
                BuyerCompanyId: null,
                // These are not editable and might not match across instances of the same field
                LayoutOverrides: field.LayoutOverrides,
            };
        }

        if (isWorkflowLevelOverride) {
            return {
                ...dirtyField,
                // These are not editable and might not match across instances of the same field
                LayoutOverrides: field.LayoutOverrides,
            };
        }

        return field;
    }

    if (outputRecordType === FieldDictionaryRecordType.BuyerLevelOverride) {
        if (isMasterField) {
            return {
                ...dirtyField,
                FieldDictionaryId: undefined,
                RecordType: FieldDictionaryRecordType.BuyerLevelOverride,
                RecordType_US: 'Buyer-level Override',
                MasterFieldDictionaryId: field.FieldDictionaryId,
                BuyerCompanyId: buyerCompanyId,
                // These are not editable and might not match across instances of the same field
                LayoutOverrides: field.LayoutOverrides,

                UsedBy: isPrintPreviewOutput ? FieldDictionaryUsedBy.BuyerPortalPrintPreview : dirtyField.UsedBy,
                UsedBy_US: isPrintPreviewOutput ? 'Buyer Portal Print Preview' : dirtyField.UsedBy_US,
            };
        }

        if (isBuyerLevelOverride) {
            if (isPrintPreviewOutput && dirtyField.UsedBy !== FieldDictionaryUsedBy.BuyerPortalPrintPreview) {
                // Create a new buyer-level override for the print preview, instead of updating the existing one
                return {
                    ...dirtyField,
                    FieldDictionaryId: undefined,
                    UsedBy: FieldDictionaryUsedBy.BuyerPortalPrintPreview,
                    UsedBy_US: 'Buyer Portal Print Preview',
                };
            }

            return {
                ...dirtyField,
                // These are not editable and might not match across instances of the same field
                LayoutOverrides: field.LayoutOverrides,
            };
        }
    }

    // We only care about master fields, buyer/workflow level overrides, and print preview overrides
    return field;
}

function cellMapper(
    cell: backendServices.ViewModels.FieldDictionaryLayoutCellViewModel,
    dirtyFields: FieldDictionaryViewModelWithMetadata[],
    buyerCompanyId: number,
    supplierCompanyId: number | null,
    outputRecordType: FieldDictionaryRecordType,
    workflowFieldDictionaryTemplateId: number | null,
    isPrintPreviewOutput: boolean
): RecursivePartial<backendServices.ViewModels.FieldDictionaryLayoutCellViewModel> {
    return {
        ...cell,
        Fields: cell.Fields.map((f) =>
            fieldMapper(
                f,
                dirtyFields,
                buyerCompanyId,
                supplierCompanyId,
                outputRecordType,
                workflowFieldDictionaryTemplateId,
                isPrintPreviewOutput
            )
        ),
        Cells: cell.Cells.map((c) =>
            cellMapper(
                c,
                dirtyFields,
                buyerCompanyId,
                supplierCompanyId,
                outputRecordType,
                workflowFieldDictionaryTemplateId,
                isPrintPreviewOutput
            )
        ),
    };
}

export function mapFormStateToPUTBody(
    documentLayout: backendServices.ViewModels.DocumentLayoutViewModel,
    dirtyFields: FieldDictionaryViewModelWithMetadata[],
    buyerCompanyId: number,
    supplierCompanyId: number | null,
    outputRecordType: FieldDictionaryRecordType,
    workflowFieldDictionaryTemplateId: number | null,
    isPrintPreviewOutput: boolean
): RecursivePartial<backendServices.ViewModels.DocumentLayoutViewModel> {
    return {
        ...documentLayout,
        Layouts: documentLayout.Layouts.map((layout) => ({
            ...layout,
            Cells: layout.Cells.map((c) =>
                cellMapper(
                    c,
                    dirtyFields,
                    buyerCompanyId,
                    supplierCompanyId,
                    outputRecordType,
                    workflowFieldDictionaryTemplateId,
                    isPrintPreviewOutput
                )
            ),
        })),
    };
}
